import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SvgIcon from '@mui/material/SvgIcon';
import Translator from '%app/js/common/services/translator';
import useAppearance from '%evp_theme/brandbook/appearance/useAppearance';
import APPEARANCE_OPTIONS, {
    APPEARANCE_LABEL_KEY,
    OPTION_ICON_SIZE,
    SELECTED_ICON_PATH,
    SELECTED_ICON_SIZE,
    findAppearanceOption,
} from '%evp_theme/brandbook/appearance/appearanceOptions';
import MaterialSymbol from '../MaterialSymbol';
import {
    COUNTRY_DIVIDER_SX,
    MENU_ITEM_SX,
    MENU_PAPER_PROPS,
    TRIGGER_SX,
} from './LocaleSelectors';

const TRIGGER_ID = 'redesign-header-appearance-trigger';
const TRIGGER_ICON_SIZE = 16;
const CHEVRON_ICON_SIZE = 14;

const OPTION_SX = [
    MENU_ITEM_SX,
    {
        gap: 1.5,
        textTransform: 'none',
    },
];

export const APPEARANCE_OPTION_LABEL_SX = { flex: 1 };

export const APPEARANCE_SELECTED_MARK_SLOT_SX = {
    display: 'inline-flex',
    width: SELECTED_ICON_SIZE,
    color: 'primary.main',
};

export function labelAppearance() {
    return Translator.trans(APPEARANCE_LABEL_KEY);
}

export function labelAppearanceOption(option) {
    return Translator.trans(option.labelKey);
}

export function AppearanceIcon({ path, size }) {
    return (
        <SvgIcon sx={{ fontSize: size }}>
            <path d={path} />
        </SvgIcon>
    );
}

AppearanceIcon.propTypes = {
    path: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
};

function AppearanceSelector() {
    const { enabled, choice, setChoice } = useAppearance();
    const [anchor, setAnchor] = useState(null);

    if (!enabled) {
        return null;
    }

    const current = findAppearanceOption(choice);
    const triggerName = `${labelAppearance()}: ${labelAppearanceOption(current)}`;

    const handleSelect = (selectedChoice) => {
        setAnchor(null);
        setChoice(selectedChoice);
    };

    return (
        <>
            <Divider orientation="vertical" flexItem sx={COUNTRY_DIVIDER_SX} />
            <Box
                component="button"
                type="button"
                id={TRIGGER_ID}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchor)}
                aria-label={triggerName}
                onClick={event => setAnchor(event.currentTarget)}
                sx={TRIGGER_SX}
                data-testid={TRIGGER_ID}
            >
                <AppearanceIcon path={current.iconPath} size={TRIGGER_ICON_SIZE} />
                <MaterialSymbol name="expand_more" size={CHEVRON_ICON_SIZE} />
            </Box>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                disableScrollLock
                PaperProps={MENU_PAPER_PROPS}
                MenuListProps={{ 'aria-labelledby': TRIGGER_ID }}
            >
                {APPEARANCE_OPTIONS.map((option) => {
                    const checked = option.choice === choice;

                    return (
                        <MenuItem
                            key={option.choice}
                            role="menuitemradio"
                            aria-checked={checked}
                            selected={checked}
                            onClick={() => handleSelect(option.choice)}
                            sx={OPTION_SX}
                        >
                            <AppearanceIcon path={option.iconPath} size={OPTION_ICON_SIZE} />
                            <Box component="span" sx={APPEARANCE_OPTION_LABEL_SX}>
                                {labelAppearanceOption(option)}
                            </Box>
                            <Box component="span" sx={APPEARANCE_SELECTED_MARK_SLOT_SX}>
                                {checked ? (
                                    <AppearanceIcon path={SELECTED_ICON_PATH} size={SELECTED_ICON_SIZE} />
                                ) : null}
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}

export default AppearanceSelector;
